import type { CellViewWire } from '@badger-sentinel/protocol';

import { WeatherGlyph } from './WeatherGlyph.js';
import { weatherFor } from '../model/selection.js';
import type { Selection } from '../model/selection.js';
import type { RegionViewWire } from '../model/view-types.js';

/**
 * The regions section of the left rail (design 297–330).
 *
 * Each row: id, name, weather now (and next, when it differs or is unknown), and
 * the count of the viewer's own assets and own contacts standing there.
 *
 * ## The counts
 *
 * Both are counts of rows the viewer already has in front of it — `friendly[]`
 * filtered by region and `contacts[]` filtered by region — so an operator could
 * reach the same numbers by scrolling. Neither is ever a fraction and neither
 * has a denominator, which is the whole of must-not #1: a number the viewer
 * cannot reconstruct is a number the projection withheld.
 *
 * ## Weather
 *
 * `weatherFor` returns three distinguishable states and this draws all three.
 * `forecast: null` is a `?`, not a repeat of the current glyph (must-not #3).
 */

export interface RegionSectionProps {
  readonly view: CellViewWire;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly selection: Selection | null;
  readonly onSelect: (selection: Selection) => void;
  readonly onContextMenu: (region: string, x: number, y: number) => void;
  readonly search: string;
}

export function RegionSection(props: RegionSectionProps): JSX.Element | null {
  const needle = props.search.trim().toLowerCase();
  const regions = props.view.map.regions.filter(
    (region) =>
      needle === '' ||
      region.id.toLowerCase().includes(needle) ||
      region.name.toLowerCase().includes(needle),
  );
  if (regions.length === 0) return null;

  return (
    <div data-testid="region-section" style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        type="button"
        data-testid="region-section-toggle"
        aria-expanded={props.open}
        onClick={props.onToggle}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 'var(--z-rail-sticky)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          height: 32,
          padding: '0 14px',
          background: 'var(--surface-2)',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 12,
            color: 'var(--text-3)',
            transform: props.open ? 'rotate(90deg)' : 'none',
          }}
        >
          ›
        </span>
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--font-ui)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-2)',
          }}
        >
          REGIONS
        </span>
        <span
          data-testid="region-count"
          style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-3)' }}
        >
          {regions.length}
        </span>
      </button>

      {props.open &&
        regions.map((region) => (
          <RegionRow
            key={region.id}
            view={props.view}
            region={region}
            selected={props.selection?.kind === 'REGION' && props.selection.id === region.id}
            onSelect={props.onSelect}
            onContextMenu={props.onContextMenu}
          />
        ))}
    </div>
  );
}

function RegionRow(props: {
  readonly view: CellViewWire;
  readonly region: RegionViewWire;
  readonly selected: boolean;
  readonly onSelect: (selection: Selection) => void;
  readonly onContextMenu: (region: string, x: number, y: number) => void;
}): JSX.Element {
  const { view, region } = props;
  const weather = weatherFor(view, region.id);
  const own = view.friendly.filter(
    (asset) => asset.location.kind === 'GROUND' && asset.location.region === region.id,
  ).length;
  const contacts = view.contacts.filter(
    (contact) => contact.location.kind === 'REGION' && contact.location.region === region.id,
  ).length;

  return (
    <button
      type="button"
      data-testid="region-row"
      data-region-id={region.id}
      aria-current={props.selected ? 'true' : undefined}
      onClick={() => props.onSelect({ kind: 'REGION', id: region.id })}
      onContextMenu={(event) => {
        event.preventDefault();
        props.onContextMenu(region.id, event.clientX, event.clientY);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        height: 26,
        padding: '0 12px',
        background: props.selected ? 'var(--accent-tint)' : 'transparent',
        border: 0,
        borderBottom: '1px solid var(--rule-faint)',
        borderLeft: '2px solid ' + (props.selected ? 'var(--accent)' : 'transparent'),
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          flex: 'none',
          width: 58,
          fontFamily: 'var(--font-data)',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.04em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: 'var(--text)',
        }}
      >
        {region.id}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--font-prose)',
          fontSize: 13,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: 'var(--text-2)',
        }}
      >
        {region.name}
      </span>

      <span
        data-testid="region-weather"
        title={weatherTitle(weather.now, weather.next)}
        style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 3 }}
      >
        <WeatherGlyph state={weather.now} outlook="now" />
        {weather.next !== null && (
          <>
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                color: 'var(--text-disabled)',
              }}
            >
              ›
            </span>
            <WeatherGlyph
              state={weather.next.known ? weather.next.state : null}
              outlook="next"
              size={15}
            />
          </>
        )}
      </span>

      <span
        data-testid="region-friendly-count"
        style={{ ...COUNT, color: 'var(--blue)' }}
        title="FRIENDLY"
      >
        {own}
      </span>
      <span
        data-testid="region-contact-count"
        style={{ ...COUNT, color: 'var(--red)' }}
        title="CONTACTS"
      >
        {contacts}
      </span>
    </button>
  );
}

function weatherTitle(
  now: string | null,
  next: { readonly known: false } | { readonly known: true; readonly state: string } | null,
): string {
  const present = 'WEATHER ' + (now ?? 'UNKNOWN');
  if (next === null) return present;
  return present + ' → ' + (next.known ? next.state : 'UNKNOWN');
}

const COUNT = {
  flex: 'none',
  width: 22,
  textAlign: 'right',
  fontFamily: 'var(--font-data)',
  fontSize: 12,
  fontWeight: 600,
} as const;
